import { Controller } from '@hotwired/stimulus'

// Holds the time a visitor picked on a scheduling link while they fill in the booking form,
// Ticketmaster style (see SchedulingLink#hold_slot!):
//
// - Picking a time holds it; picking another time moves the hold. Closing the form or leaving the
//   page releases it, and the back button re-takes it for a form that's still open.
// - A heartbeat every 30 seconds keeps it; the server lets it lapse 90 seconds after the last one.
// - When someone else just took the time, say so and close the form, which refreshes the slots.
// - Skipped inside a frame: the holder cookie may not be sent there, and without it the visitor's
//   own hold would count against them.
//
// Requests go out one at a time, in the order the visitor acted, so a slow response for an earlier
// pick can never undo a later one. A release names its slot, so it can't drop a newer hold.
const HEARTBEAT_MS = 30_000

export default class extends Controller {
  static targets = ['taken']
  static values = { holdUrl: String, heartbeatUrl: String, releaseUrl: String }

  connect() {
    if (window.self !== window.top) return

    this.slot = null // the slot the form is open for: { startsAt, endsAt }
    this.heldSlot = null
    this.queue = Promise.resolve()
    this.onShow = this.onShow.bind(this)
    this.onHide = this.onHide.bind(this)
    this.onPageHide = this.onPageHide.bind(this)
    this.onPageShow = this.onPageShow.bind(this)
    document.addEventListener('booking-form:show', this.onShow)
    document.addEventListener('booking-form:hide', this.onHide)
    window.addEventListener('pagehide', this.onPageHide)
    window.addEventListener('pageshow', this.onPageShow)
  }

  disconnect() {
    document.removeEventListener('booking-form:show', this.onShow)
    document.removeEventListener('booking-form:hide', this.onHide)
    window.removeEventListener('pagehide', this.onPageHide)
    window.removeEventListener('pageshow', this.onPageShow)
    this.slot = null
    this.stopHeartbeat()
  }

  onShow(event) {
    const { startsAt, endsAt } = event.detail || {}
    if (!startsAt || startsAt === this.slot?.startsAt) return

    const slot = { startsAt, endsAt }
    this.slot = slot
    this.hideTaken()
    this.enqueue(() => this.hold(slot))
  }

  onHide() {
    const slot = this.slot
    if (!slot) return

    this.slot = null
    this.enqueue(() => this.release(slot))
  }

  onPageHide() {
    if (!this.heldSlot) return

    const data = new FormData()
    data.append(this.csrfParam(), this.csrfToken())
    data.append('starts_at', this.heldSlot.startsAt)
    navigator.sendBeacon(this.releaseUrlValue, data)
    this.dropHold()
  }

  onPageShow(event) {
    const slot = this.slot
    if (event.persisted && slot) this.enqueue(() => this.hold(slot))
  }

  enqueue(request) {
    this.queue = this.queue.then(request).catch(() => {
      // No hold: booking still re-checks the time under the host's lock.
    })
  }

  async hold(slot) {
    if (this.slot !== slot) return // already replaced or closed before its turn

    const data = await this.post(this.holdUrlValue, {
      starts_at: slot.startsAt,
      ends_at: slot.endsAt,
    })
    if (!data) return

    if (this.slot !== slot) {
      // Replaced or closed while in flight: give this one straight back.
      if (data.held)
        await this.post(this.releaseUrlValue, { starts_at: slot.startsAt })
      return
    }
    if (data.held) {
      this.heldSlot = slot
      this.startHeartbeat()
    } else {
      this.dropHold()
      if (data.reason === 'unheld') return // keep the form open; booking still re-checks under the lock
      this.showTaken()
      document.dispatchEvent(new CustomEvent('capacity-hold-slot:taken'))
    }
  }

  async release(slot) {
    if (this.heldSlot === slot) this.dropHold()
    await this.post(this.releaseUrlValue, { starts_at: slot.startsAt })
  }

  dropHold() {
    this.heldSlot = null
    this.stopHeartbeat()
  }

  startHeartbeat() {
    this.stopHeartbeat()
    this.heartbeatTimer = setInterval(() => {
      this.post(this.heartbeatUrlValue)
        .then((data) => {
          if (data && !data.held) this.dropHold()
        })
        .catch(() => {
          // A missed beat is fine: the hold only lapses after 90 seconds without one.
        })
    }, HEARTBEAT_MS)
  }

  stopHeartbeat() {
    clearInterval(this.heartbeatTimer)
    this.heartbeatTimer = null
  }

  showTaken() {
    this.takenTargets.forEach((target) => (target.hidden = false))
  }

  hideTaken() {
    this.takenTargets.forEach((target) => (target.hidden = true))
  }

  post(url, params = {}) {
    const body = new FormData()
    Object.entries(params).forEach(([key, value]) => body.append(key, value))
    return fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      body,
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'X-CSRF-Token': this.csrfToken(),
        Accept: 'application/json',
      },
    }).then((response) =>
      // A release answers with an empty body.
      response.ok ? response.json().catch(() => ({})) : null
    )
  }

  csrfParam() {
    return document.querySelector('meta[name=csrf-param]')?.content
  }

  csrfToken() {
    return document.querySelector('meta[name=csrf-token]')?.content
  }
}
